import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { href, useNavigate } from 'react-router';
import isServer from '~/scripts/isServer';
import { logger } from '~/scripts/logger';

export interface RouteLocationOptions {
  path: Parameters<typeof href>[0] | string;
  query?: Record<string, unknown>;
  hash?: string;
}

export interface Router {
  push(to: string | RouteLocationOptions): void;
  replace(to: string | RouteLocationOptions): void;
  reload(): void;
}

const doNavigation = (
  navigate: ReturnType<typeof useNavigate>,
  to: string | RouteLocationOptions,
  replace: boolean = false
) => {
  logger.info(`[router.${replace ? 'replace' : 'push'}]`, JSON.stringify(to));
  if (typeof to === 'string') {
    navigate(to, { replace });
  } else {
    const { path, query, hash } = to;
    const queryString = qs.stringify(query, {
      arrayFormat: 'indices',
      encode: false,
    });
    navigate(
      {
        pathname: path,
        search: queryString ? `?${queryString}` : '',
        hash: hash,
      },
      { replace }
    );
  }
};

export default function useRouterDefault() {
  const navigate = useNavigate();

  const push = useCallback(
    (to: string | RouteLocationOptions) => {
      doNavigation(navigate, to, false);
    },
    [navigate]
  );

  const replace = useCallback(
    (to: string | RouteLocationOptions) => {
      doNavigation(navigate, to, true);
    },
    [navigate]
  );

  const reload = useCallback(() => {
    if (!isServer()) {
      window.location.reload();
    }
  }, []);

  const router = useMemo(
    (): Router => ({
      push,
      replace,
      reload,
    }),
    [push, replace, reload]
  );

  return router;
}
